import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  chargerPageHistorique,
  useHistorique,
  type JeuHistorique,
  type LignesHistorique,
} from '../api/queries';
import type {
  FiltreHistoriqueTramesRequete,
  LigneImpulsionHistorique,
  LigneMesureHistorique,
  LigneTrameHistorique,
} from '../api/types';
import { Badge } from '../components/Badges';
import { Bouton } from '../components/Boutons';
import { Carte, Definitions } from '../components/Carte';
import { Interrupteur, Saisie, Segmente, Selecteur } from '../components/Champs';
import { JsonBrut } from '../components/JsonBrut';
import { MessageErreur, texteErreur } from '../components/MessageErreur';
import { Modale } from '../components/Modale';
import { useNotifications } from '../components/Notifications';
import { Tableau, type Colonne } from '../components/Tableau';
import { FUSEAU_AFFICHAGE } from '../config';
import { VALEUR_ABSENTE } from '../i18n/fr';
import { construireCsv } from '../lib/csv';
import { horodatageFichier, telechargerTexte } from '../lib/divers';
import { formaterDateHeureSecondes, formaterDecimal, formaterNombre, versIsoUtc } from '../lib/format';
import { useDifferer } from '../lib/useDifferer';
import { telechargerXlsx, type CelluleTableur } from '../lib/xlsx';

/* -------------------------------------------------------------------------- */
/* Période                                                                     */
/* -------------------------------------------------------------------------- */

type Periode = '1h' | '24h' | '7j' | '30j' | 'tout' | 'dates';

const PERIODES: ReadonlyArray<{ valeur: Periode; libelle: string; minutes?: number }> = [
  { valeur: '1h', libelle: '1 h', minutes: 60 },
  { valeur: '24h', libelle: '24 h', minutes: 1440 },
  { valeur: '7j', libelle: '7 j', minutes: 10_080 },
  { valeur: '30j', libelle: '30 j', minutes: 43_200 },
  { valeur: 'tout', libelle: 'Tout' },
  { valeur: 'dates', libelle: 'Dates' },
];

const JEUX: ReadonlyArray<{ valeur: JeuHistorique; libelle: string }> = [
  { valeur: 'mesures', libelle: 'Mesures' },
  { valeur: 'trames', libelle: 'Trames MQTT' },
  { valeur: 'impulsions', libelle: 'Mesures précises' },
];

const TYPES_TRAME = ['HEARTBEAT', 'MEASURE', 'POSITION', 'EVENT', 'ACK', 'BOOT', 'DIAG'] as const;

const TAILLE_PAGE_HISTORIQUE = 200;
const TAILLE_PAGE_EXPORT = 1000;
/** Au-delà, l'export est tronqué : un navigateur ne tient pas un million de trames en mémoire. */
const MAX_LIGNES_EXPORT = 100_000;

/** `datetime-local` du poste pour une date. */
function versSaisieLocale(date: Date): string {
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}T${p(date.getHours())}:${p(date.getMinutes())}`;
}

/* -------------------------------------------------------------------------- */
/* Mise en forme commune écran / export                                        */
/* -------------------------------------------------------------------------- */

const fmtMillisecondes = new Intl.DateTimeFormat('fr-FR', {
  timeZone: FUSEAU_AFFICHAGE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  fractionalSecondDigits: 3,
  hourCycle: 'h23',
});

/** `21/10/2026 09:24:52,616` — la milliseconde compte entre deux impulsions. */
function formaterAvecMs(iso: string | null | undefined): string {
  if (!iso) return VALEUR_ABSENTE;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? VALEUR_ABSENTE : fmtMillisecondes.format(d);
}

function canal(t: LigneTrameHistorique): string {
  const c = t.payload['canal'];
  return typeof c === 'string' ? c : '';
}

function contenu(t: LigneTrameHistorique): unknown {
  return 'data' in t.payload ? t.payload['data'] : t.payload;
}

function resume(t: LigneTrameHistorique): string {
  const texte = JSON.stringify(contenu(t)) ?? '';
  return texte.length > 160 ? `${texte.slice(0, 160)}…` : texte;
}

const versDateOuNull = (iso: string | null | undefined): Date | null => (iso ? new Date(iso) : null);

/** Colonnes d'export : en-têtes et cellules typées (dates en dates, nombres en nombres). */
interface Export<T> {
  feuille: string;
  entetes: string[];
  ligne: (item: T) => CelluleTableur[];
}

const EXPORT_MESURES: Export<LigneMesureHistorique> = {
  feuille: 'Mesures',
  entetes: [
    'mesure_at',
    'recu_at',
    'tension_kv',
    'tension_min_kv',
    'tension_max_kv',
    'nb_impulsions',
    'nb_impulsions_sous_seuil',
    'nb_impulsions_absentes',
    'sous_seuil',
    'source',
    'message_id',
  ],
  ligne: (m) => [
    versDateOuNull(m.mesure_at),
    versDateOuNull(m.recu_at),
    m.tension_kv,
    m.tension_min_kv,
    m.tension_max_kv,
    m.nb_impulsions,
    m.nb_impulsions_sous_seuil,
    m.nb_impulsions_absentes,
    m.sous_seuil,
    m.source,
    m.message_id,
  ],
};

const EXPORT_TRAMES: Export<LigneTrameHistorique> = {
  feuille: 'Trames MQTT',
  entetes: [
    'recu_at',
    'emis_at',
    'type',
    'canal',
    'boot',
    'seq',
    'message_id',
    'taille_octets',
    'signature_valide',
    'traite_at',
    'erreur_traitement',
    'charge_utile',
  ],
  ligne: (t) => [
    versDateOuNull(t.recu_at),
    versDateOuNull(t.emis_at),
    t.type,
    canal(t),
    t.boot,
    t.seq,
    t.message_id,
    t.taille_octets,
    t.signature_valide,
    versDateOuNull(t.traite_at),
    t.erreur_traitement,
    JSON.stringify(contenu(t)),
  ],
};

/** Les profils sont dépliés en colonnes, une par instant : on peut les tracer dans Excel. */
const COLONNES_PROFIL_COURT = Array.from({ length: 64 }, (_, i) => `pc_${i * 10}us`);
const COLONNES_PROFIL_LONG = Array.from({ length: 50 }, (_, i) => `pl_${i * 80}us`);

const EXPORT_IMPULSIONS: Export<LigneImpulsionHistorique> = {
  feuille: 'Mesures précises',
  entetes: [
    'impulsion_at',
    'nom_mesure',
    'commande_id',
    'numero',
    'intervalle_ms',
    'rejetee',
    'tension_kv',
    'pic_lsb',
    'lobe_positif_lsb',
    'aire_lsb_ms',
    'largeur_us',
    'creux_us',
    'nb_echantillons',
    'ligne_base_lsb',
    'seuil_lsb',
    'bruit_lsb',
    'nb_sat_bas',
    'nb_sat_haut',
    'echantillons_s',
    'kv_par_lsb_ms',
    'recu_at',
    ...COLONNES_PROFIL_COURT,
    ...COLONNES_PROFIL_LONG,
  ],
  ligne: (i) => [
    versDateOuNull(i.impulsion_at),
    i.nom_mesure,
    i.commande_id,
    i.numero,
    i.intervalle_ms,
    i.rejetee,
    i.tension_kv,
    i.pic_lsb,
    i.lobe_positif_lsb,
    i.aire_lsb_ms,
    i.largeur_us,
    i.creux_us,
    i.nb_echantillons,
    i.ligne_base_lsb,
    i.seuil_lsb,
    i.bruit_lsb,
    i.nb_sat_bas,
    i.nb_sat_haut,
    i.echantillons_s,
    i.kv_par_lsb_ms,
    versDateOuNull(i.recu_at),
    ...COLONNES_PROFIL_COURT.map((_, k) => i.profil_court[k] ?? null),
    ...COLONNES_PROFIL_LONG.map((_, k) => i.profil_long[k] ?? null),
  ],
};

const EXPORTS: { [J in JeuHistorique]: Export<LignesHistorique[J]> } = {
  mesures: EXPORT_MESURES,
  trames: EXPORT_TRAMES,
  impulsions: EXPORT_IMPULSIONS,
};

/** Cellule CSV pour un Excel français : virgule décimale, dates lisibles, oui / non. */
function celluleCsv(v: CelluleTableur): string | number | null {
  if (v === null || v === undefined) return null;
  if (v instanceof Date) return formaterAvecMs(v.toISOString());
  if (typeof v === 'boolean') return v ? 'oui' : 'non';
  if (typeof v === 'number') return String(v).replace('.', ',');
  return v;
}

/* -------------------------------------------------------------------------- */
/* Petit tracé du profil d'impulsion                                           */
/* -------------------------------------------------------------------------- */

function Profil({ valeurs }: { valeurs: readonly number[] }): JSX.Element {
  if (valeurs.length < 2) return <>{VALEUR_ABSENTE}</>;
  const largeur = 120;
  const hauteur = 28;
  const min = Math.min(0, ...valeurs);
  const max = Math.max(1, ...valeurs);
  const y = (v: number): number => hauteur - ((v - min) / (max - min)) * hauteur;
  const points = valeurs
    .map((v, i) => `${((i / (valeurs.length - 1)) * largeur).toFixed(1)},${y(v).toFixed(1)}`)
    .join(' ');
  return (
    <svg width={largeur} height={hauteur} role="img" aria-label="Profil de l’impulsion sur 4 ms">
      <line x1={0} x2={largeur} y1={y(0)} y2={y(0)} stroke="var(--gv-bordure)" strokeWidth={1} />
      <polyline points={points} fill="none" stroke="var(--gv-primaire, #1F6FB2)" strokeWidth={1.4} />
    </svg>
  );
}

/* -------------------------------------------------------------------------- */
/* Page                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Toutes les données d'un appareil : mesures de tension, trames MQTT reçues et impulsions des
 * « Mesures précises », sur une période choisie, avec export CSV et Excel.
 *
 * La fiche appareil reste l'écran du support (dernières trames, état, commandes) ; cette page
 * est celle de l'analyse : tout l'historique, page par page, et l'export complet de la période.
 */
export function DonneesAppareilPage(): JSX.Element {
  const { numeroSerie = '' } = useParams();
  const sn = decodeURIComponent(numeroSerie);
  const notifications = useNotifications();

  const [jeu, setJeu] = useState<JeuHistorique>('mesures');
  const [periode, setPeriode] = useState<Periode>('24h');
  const [reference, setReference] = useState(() => Date.now());
  const [saisieDu, setSaisieDu] = useState(() => versSaisieLocale(new Date(Date.now() - 86_400_000)));
  const [saisieAu, setSaisieAu] = useState(() => versSaisieLocale(new Date()));
  const [typeTrame, setTypeTrame] = useState('');
  const [rechercheSaisie, setRechercheSaisie] = useState('');
  const recherche = useDifferer(rechercheSaisie.trim());
  const [direct, setDirect] = useState(false);
  const [masquerRejetees, setMasquerRejetees] = useState(true);
  const [detail, setDetail] = useState<LigneTrameHistorique | LigneImpulsionHistorique | null>(null);
  const [exportEnCours, setExportEnCours] = useState<{ fait: number; total: number } | null>(null);

  const filtres = useMemo<FiltreHistoriqueTramesRequete>(() => {
    const f: FiltreHistoriqueTramesRequete = { limite: TAILLE_PAGE_HISTORIQUE };
    if (periode === 'dates') {
      const du = versIsoUtc(saisieDu);
      const au = versIsoUtc(saisieAu);
      if (du) f.du = du;
      if (au) f.au = au;
    } else {
      const minutes = PERIODES.find((p) => p.valeur === periode)?.minutes;
      if (minutes !== undefined) f.du = new Date(reference - minutes * 60_000).toISOString();
    }
    if (jeu === 'trames') {
      if (typeTrame) f.type = typeTrame;
      if (recherche) f.recherche = recherche;
    }
    return f;
  }, [periode, saisieDu, saisieAu, reference, jeu, typeTrame, recherche]);

  const requete = useHistorique(sn, jeu, filtres, { direct: direct && periode !== 'dates' });
  const pages = requete.data?.pages ?? [];
  const total = pages[0]?.total ?? 0;
  const toutes = pages.flatMap((p) => p.items);

  const actualiser = (): void => {
    setReference(Date.now());
    void requete.refetch();
  };

  const exporter = async (format: 'csv' | 'xlsx'): Promise<void> => {
    const definition = EXPORTS[jeu] as Export<LignesHistorique[typeof jeu]>;
    const items: Array<LignesHistorique[typeof jeu]> = [];
    const aExporter = Math.min(total, MAX_LIGNES_EXPORT);
    setExportEnCours({ fait: 0, total: aExporter });
    try {
      let curseur: string | undefined;
      do {
        const page = await chargerPageHistorique(sn, jeu, {
          ...filtres,
          limite: TAILLE_PAGE_EXPORT,
          ...(curseur ? { curseur } : {}),
        });
        items.push(...page.items);
        curseur = page.curseur_suivant ?? undefined;
        setExportEnCours({ fait: items.length, total: aExporter });
      } while (curseur && items.length < MAX_LIGNES_EXPORT);

      // Ordre chronologique dans le fichier : c'est celui qu'on veut pour tracer une courbe.
      const lignes = items.slice(0, MAX_LIGNES_EXPORT).reverse().map(definition.ligne);
      const base = `creb-${sn}-${jeu}-${horodatageFichier()}`;
      if (format === 'csv') {
        telechargerTexte(
          `${base}.csv`,
          construireCsv(definition.entetes, lignes.map((l) => l.map(celluleCsv))),
        );
      } else {
        telechargerXlsx(`${base}.xlsx`, [{ nom: definition.feuille, entetes: definition.entetes, lignes }]);
      }
      notifications.succes(
        items.length >= MAX_LIGNES_EXPORT && total > MAX_LIGNES_EXPORT
          ? `Export limité aux ${formaterNombre(MAX_LIGNES_EXPORT)} lignes les plus récentes sur ${formaterNombre(total)}. Réduisez la période pour le reste.`
          : `${formaterNombre(lignes.length)} ligne(s) exportée(s).`,
      );
    } catch (erreur) {
      notifications.erreur(texteErreur(erreur));
    } finally {
      setExportEnCours(null);
    }
  };

  /* --- Colonnes ---------------------------------------------------------- */

  const colonnesMesures: Array<Colonne<LigneMesureHistorique>> = [
    {
      cle: 'mesure',
      entete: 'Mesure',
      rendu: (m) => <span className="gv-mono gv-corps-s">{formaterDateHeureSecondes(m.mesure_at)}</span>,
    },
    {
      cle: 'tension',
      entete: 'Tension',
      numerique: true,
      rendu: (m) => <strong>{formaterDecimal(m.tension_kv, 2)} kV</strong>,
    },
    {
      cle: 'min',
      entete: 'Min',
      numerique: true,
      rendu: (m) => (m.tension_min_kv === null ? VALEUR_ABSENTE : formaterDecimal(m.tension_min_kv, 2)),
    },
    {
      cle: 'max',
      entete: 'Max',
      numerique: true,
      rendu: (m) => (m.tension_max_kv === null ? VALEUR_ABSENTE : formaterDecimal(m.tension_max_kv, 2)),
    },
    { cle: 'nb', entete: 'Impulsions', numerique: true, rendu: (m) => m.nb_impulsions },
    {
      cle: 'sous',
      entete: 'Sous seuil',
      numerique: true,
      rendu: (m) => m.nb_impulsions_sous_seuil ?? VALEUR_ABSENTE,
    },
    {
      cle: 'absentes',
      entete: 'Absentes',
      numerique: true,
      rendu: (m) => m.nb_impulsions_absentes ?? VALEUR_ABSENTE,
    },
    {
      cle: 'source',
      entete: 'Source',
      rendu: (m) =>
        m.source === 'SESSION_TEMPS_REEL' ? (
          <Badge ton="bleu" libelle="Suivi en direct" />
        ) : (
          <Badge libelle="Périodique" />
        ),
    },
    {
      cle: 'recu',
      entete: 'Reçue',
      rendu: (m) => <span className="gv-mono gv-corps-s gv-secondaire">{formaterDateHeureSecondes(m.recu_at)}</span>,
    },
  ];

  const colonnesTrames: Array<Colonne<LigneTrameHistorique>> = [
    {
      cle: 'recu',
      entete: 'Reçue',
      rendu: (t) => <span className="gv-mono gv-corps-s">{formaterDateHeureSecondes(t.recu_at)}</span>,
    },
    { cle: 'type', entete: 'Type', rendu: (t) => <Badge libelle={t.type} avecPoint={false} /> },
    { cle: 'canal', entete: 'Canal', rendu: (t) => <span className="gv-mono gv-corps-s">{canal(t) || VALEUR_ABSENTE}</span> },
    {
      cle: 'contenu',
      entete: 'Contenu',
      rendu: (t) => (
        <span className="gv-mono gv-corps-s" style={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>
          {resume(t)}
        </span>
      ),
    },
    {
      cle: 'traitement',
      entete: 'Traitement',
      rendu: (t) =>
        t.erreur_traitement ? (
          <span className="gv-mono gv-corps-s" style={{ color: 'var(--gv-rouge)' }}>
            {t.erreur_traitement}
          </span>
        ) : t.traite_at ? (
          <Badge ton="vert" libelle="OK" />
        ) : (
          <Badge ton="orange" libelle="En attente" />
        ),
    },
    { cle: 'boot', entete: 'Boot / seq', numerique: true, rendu: (t) => `${t.boot} / ${t.seq}` },
  ];

  const colonnesImpulsions: Array<Colonne<LigneImpulsionHistorique>> = [
    {
      cle: 'at',
      entete: 'Impulsion',
      rendu: (i) => <span className="gv-mono gv-corps-s">{formaterAvecMs(i.impulsion_at)}</span>,
    },
    {
      cle: 'session',
      entete: 'Mesure',
      rendu: (i) =>
        i.nom_mesure ?? (
          <span className="gv-mono gv-corps-s gv-secondaire">{i.commande_id?.slice(0, 8) ?? VALEUR_ABSENTE}</span>
        ),
    },
    {
      cle: 'kv',
      entete: 'kV',
      numerique: true,
      rendu: (i) => (i.tension_kv === null ? VALEUR_ABSENTE : <strong>{formaterDecimal(i.tension_kv, 2)}</strong>),
    },
    { cle: 'pic', entete: 'Pic', numerique: true, rendu: (i) => i.pic_lsb ?? VALEUR_ABSENTE },
    {
      cle: 'aire',
      entete: 'Aire',
      numerique: true,
      rendu: (i) => (i.aire_lsb_ms === null ? VALEUR_ABSENTE : formaterDecimal(i.aire_lsb_ms, 1)),
    },
    { cle: 'largeur', entete: <>Largeur <span style={{ textTransform: 'none' }}>(µs)</span></>, numerique: true, rendu: (i) => i.largeur_us ?? VALEUR_ABSENTE },
    { cle: 'intervalle', entete: <>Intervalle <span style={{ textTransform: 'none' }}>(ms)</span></>, numerique: true, rendu: (i) => i.intervalle_ms ?? VALEUR_ABSENTE },
    {
      cle: 'bruit',
      entete: 'Bruit',
      numerique: true,
      rendu: (i) => (i.bruit_lsb === null ? VALEUR_ABSENTE : formaterDecimal(i.bruit_lsb, 1)),
    },
    { cle: 'profil', entete: 'Profil 4 ms', rendu: (i) => <Profil valeurs={i.profil_long} /> },
    {
      cle: 'rejetee',
      entete: 'Statut',
      rendu: (i) => (i.rejetee ? <Badge ton="orange" libelle="Rejetée" /> : <Badge ton="vert" libelle="Retenue" />),
    },
  ];

  /* --- Rendu ------------------------------------------------------------- */

  const libelleJeu = JEUX.find((j) => j.valeur === jeu)?.libelle ?? '';
  const impulsionsVisibles =
    jeu === 'impulsions' && masquerRejetees
      ? (toutes as LigneImpulsionHistorique[]).filter((i) => !i.rejetee)
      : (toutes as LigneImpulsionHistorique[]);

  const pied =
    toutes.length > 0 ? (
      <div className="gv-ligne" style={{ padding: '12px 16px', justifyContent: 'space-between' }}>
        <span className="gv-corps-s gv-secondaire">
          {formaterNombre(toutes.length)} affichée(s) sur {formaterNombre(total)}
        </span>
        {requete.hasNextPage ? (
          <Bouton
            occupe={requete.isFetchingNextPage}
            onClick={() => {
              void requete.fetchNextPage();
            }}
          >
            Charger {formaterNombre(Math.min(TAILLE_PAGE_HISTORIQUE, total - toutes.length))} de plus
          </Bouton>
        ) : null}
      </div>
    ) : undefined;

  const communs = {
    chargement: requete.isPending || (requete.isFetching && toutes.length === 0),
    titreVide: 'Aucune donnée',
    descriptionVide: 'Rien sur cette période. Élargissez la période ou changez de filtre.',
    ...(pied ? { pied } : {}),
  };

  return (
    <>
      <header className="gv-entete">
        <div className="gv-pile gv-pile--serree">
          <span className="gv-corps-s">
            <Link to={`/appareils/${encodeURIComponent(sn)}`}>← Fiche appareil</Link>
          </span>
          <h1 className="gv-h1">
            Toutes les données · <span className="gv-mono">{sn}</span>
          </h1>
        </div>
        <div className="gv-entete__actions">
          <Bouton
            disabled={total === 0 || exportEnCours !== null}
            onClick={() => {
              void exporter('csv');
            }}
          >
            Exporter CSV
          </Bouton>
          <Bouton
            variante="primaire"
            disabled={total === 0 || exportEnCours !== null}
            onClick={() => {
              void exporter('xlsx');
            }}
          >
            Exporter Excel
          </Bouton>
        </div>
      </header>

      {exportEnCours ? (
        <div className="gv-bandeau">
          Export de « {libelleJeu} » en cours : {formaterNombre(exportEnCours.fait)} /{' '}
          {formaterNombre(exportEnCours.total)} lignes…
        </div>
      ) : null}

      <Carte>
        <div className="gv-pile">
          <div className="gv-ligne" style={{ flexWrap: 'wrap', gap: 12 }}>
            <Segmente libelle="Données" valeur={jeu} options={JEUX} onChange={setJeu} />
            <Segmente
              libelle="Période"
              valeur={periode}
              options={PERIODES.map((p) => ({ valeur: p.valeur, libelle: p.libelle }))}
              onChange={(p) => {
                setPeriode(p);
                setReference(Date.now());
              }}
            />
            <Interrupteur
              libelle="En direct (10 s)"
              actif={direct && periode !== 'dates'}
              disabled={periode === 'dates'}
              onChange={setDirect}
            />
            <Bouton occupe={requete.isFetching && !requete.isFetchingNextPage} onClick={actualiser}>
              Actualiser
            </Bouton>
          </div>

          {periode === 'dates' ? (
            <div className="gv-ligne" style={{ flexWrap: 'wrap', gap: 12 }}>
              <Saisie
                label="Du"
                type="datetime-local"
                value={saisieDu}
                onChange={(e) => {
                  setSaisieDu(e.currentTarget.value);
                }}
              />
              <Saisie
                label="Au"
                type="datetime-local"
                value={saisieAu}
                onChange={(e) => {
                  setSaisieAu(e.currentTarget.value);
                }}
              />
            </div>
          ) : null}

          {jeu === 'trames' ? (
            <div className="gv-ligne" style={{ flexWrap: 'wrap', gap: 12 }}>
              <Selecteur
                label="Type"
                optionVide="Tous les types"
                options={TYPES_TRAME.map((t) => ({ valeur: t, libelle: t }))}
                value={typeTrame}
                onChange={(e) => {
                  setTypeTrame(e.currentTarget.value);
                }}
              />
              <Saisie
                label="Rechercher dans le contenu"
                placeholder="POWER_ON, cmd_id, gn…"
                value={rechercheSaisie}
                onChange={(e) => {
                  setRechercheSaisie(e.currentTarget.value);
                }}
              />
            </div>
          ) : null}

          {jeu === 'impulsions' ? (
            <Interrupteur
              libelle="Masquer les détections rejetées (bruit)"
              actif={masquerRejetees}
              onChange={setMasquerRejetees}
            />
          ) : null}

          <span className="gv-corps-s gv-secondaire">
            {requete.isPending
              ? 'Chargement…'
              : `${formaterNombre(total)} ligne(s) sur la période. Les exports reprennent toute la période et tous les filtres${
                  jeu === 'impulsions' ? ', détections rejetées comprises' : ''
                }, dans l’ordre chronologique (${formaterNombre(MAX_LIGNES_EXPORT)} lignes au plus).`}
          </span>
        </div>
      </Carte>

      {requete.error ? <MessageErreur erreur={requete.error} /> : null}

      <Carte titre={libelleJeu} sansPadding>
        {jeu === 'mesures' ? (
          <Tableau
            colonnes={colonnesMesures}
            lignes={toutes as LigneMesureHistorique[]}
            cleDe={(m) => String(m.id)}
            {...communs}
          />
        ) : jeu === 'trames' ? (
          <Tableau
            colonnes={colonnesTrames}
            lignes={toutes as LigneTrameHistorique[]}
            cleDe={(t) => String(t.id)}
            surClic={setDetail}
            {...communs}
          />
        ) : (
          <Tableau
            colonnes={colonnesImpulsions}
            lignes={impulsionsVisibles}
            cleDe={(i) => String(i.id)}
            surClic={setDetail}
            {...communs}
          />
        )}
      </Carte>

      <Modale
        titre={detail ? ('payload' in detail ? `Trame ${detail.type}` : 'Impulsion') : ''}
        ouverte={detail !== null}
        surFermeture={() => {
          setDetail(null);
        }}
        large
      >
        {detail === null ? null : 'payload' in detail ? (
          <>
            <Definitions
              entrees={[
                { cle: 'Identifiant de message', valeur: <span className="gv-mono">{detail.message_id}</span> },
                { cle: 'Boot / séquence', valeur: `${detail.boot} / ${detail.seq}` },
                { cle: 'Émise (objet)', valeur: formaterDateHeureSecondes(detail.emis_at) },
                { cle: 'Reçue (serveur)', valeur: formaterAvecMs(detail.recu_at) },
                { cle: 'Taille', valeur: detail.taille_octets === null ? VALEUR_ABSENTE : `${detail.taille_octets} octets` },
              ]}
            />
            <JsonBrut valeur={detail.payload} libelleCopie="Copier la trame" />
          </>
        ) : (
          <>
            <Definitions
              entrees={[
                { cle: 'Mesure', valeur: detail.nom_mesure ?? detail.commande_id ?? VALEUR_ABSENTE },
                { cle: 'Impulsion', valeur: formaterAvecMs(detail.impulsion_at) },
                {
                  cle: 'Tension',
                  valeur: detail.tension_kv === null ? VALEUR_ABSENTE : `${formaterDecimal(detail.tension_kv, 2)} kV`,
                },
                { cle: 'Statut', valeur: detail.rejetee ? 'Rejetée (bruit)' : 'Retenue' },
              ]}
            />
            <JsonBrut valeur={detail} libelleCopie="Copier l’impulsion" />
          </>
        )}
      </Modale>
    </>
  );
}
